/**
 * Geocoding Service
 * 
 * This service handles geocoding requests using the backend Mapbox integration
 */

import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:5000';

export default {
  /**
   * Geocode a batch of addresses
   * @param {Array} addresses Array of address data objects
   * @param {string} addressMode 'single' or 'components'
   * @param {string} countryCode Optional 2-letter country code
   * @returns {Promise} Promise with geocoding results
   */
  async geocodeAddresses(addresses, addressMode = 'single', countryCode = null) {
    try {
      const requestData = {
        addresses,
        addressMode,
        countryCode
      };

      const response = await axios.post(`${API_URL}/api/locations/geocode`, requestData);
      return response.data;
    } catch (error) {
      console.error('Error geocoding addresses:', error);
      throw new Error(error.response?.data?.error || 'Geocoding failed');
    }
  },

  /**
   * Construct address strings from CSV data for preview
   * @param {Array} csvData Array of CSV row objects
   * @param {Object} addressMapping Address field mapping configuration
   * @returns {Array} Array of constructed address strings
   */
  constructAddressesFromCSV(csvData, addressMapping) {
    return csvData.map((row, index) => {
      try {
        if (addressMapping.mode === 'single') {
          const addressField = addressMapping.fullAddress;
          const address = row[addressField] || '';
          return {
            index,
            address: String(address).trim(),
            components: null,
            valid: Boolean(String(address).trim())
          };
        } else {
          // Components mode
          const components = {};
          const componentFields = addressMapping.components || {};
          
          for (const [componentName, fieldName] of Object.entries(componentFields)) {
            if (fieldName && row[fieldName]) {
              components[componentName] = String(row[fieldName]).trim();
            }
          }
          
          const address = this.constructAddressFromComponents(components);
          
          return {
            index,
            address,
            components,
            valid: Boolean(address)
          };
        }
      } catch (error) {
        return {
          index,
          address: '',
          components: null,
          valid: false,
          error: error.message
        };
      }
    });
  },

  /**
   * Construct a single address from components
   * @param {Object} components Address components object
   * @returns {string} Constructed address string
   */
  constructAddressFromComponents(components) {
    const parts = [];

    // Order matters for geocoding quality
    if (components.street) parts.push(components.street);
    if (components.city) parts.push(components.city);
    if (components.state) parts.push(components.state);
    if (components.postal_code) parts.push(components.postal_code);
    if (components.country) parts.push(components.country);

    return parts.filter(part => part && part.trim()).join(', ');
  },

  /**
   * Auto-detect potential address columns from CSV headers
   * @param {Array} columns Array of column names
   * @returns {Object} Suggested address field mappings
   */
  autoDetectAddressColumns(columns) {
    const lowerColumns = columns.map(col => col.toLowerCase());
    const suggestions = {
      fullAddress: null,
      street: null,
      city: null,
      state: null,
      country: null,
      postal_code: null
    };

    // Address field patterns
    const patterns = {
      fullAddress: ['address', 'full_address', 'street_address', 'location', 'addr'],
      street: ['street', 'street_address', 'address_line', 'addr1', 'street_name'],
      city: ['city', 'town', 'municipality', 'locality'],
      state: ['state', 'province', 'region', 'admin_area'],
      country: ['country', 'nation', 'country_code'],
      postal_code: ['zip', 'postal', 'postcode', 'postal_code', 'zip_code']
    };

    // Find best matches
    for (const [field, fieldPatterns] of Object.entries(patterns)) {
      for (const pattern of fieldPatterns) {
        const matchIndex = lowerColumns.findIndex(col => 
          col.includes(pattern) || pattern.includes(col)
        );
        if (matchIndex !== -1) {
          suggestions[field] = columns[matchIndex];
          break;
        }
      }
    }

    return suggestions;
  },

  /**
   * Validate geocoding results and categorize them
   * @param {Array} results Geocoding results from API
   * @returns {Object} Validation summary with categories
   */
  validateGeocodingResults(results) {
    const categories = {
      successful: [],
      failed: [],
      lowConfidence: []
    };

    const lowConfidenceThreshold = 60; // Below 60% confidence is considered low

    results.forEach(result => {
      if (result.success) {
        if (result.confidence < lowConfidenceThreshold) {
          categories.lowConfidence.push(result);
        } else {
          categories.successful.push(result);
        }
      } else {
        categories.failed.push(result);
      }
    });

    return {
      categories,
      summary: {
        total: results.length,
        successful: categories.successful.length,
        failed: categories.failed.length,
        lowConfidence: categories.lowConfidence.length,
        successRate: (categories.successful.length / results.length) * 100
      }
    };
  },

  /**
   * Prepare geocoding data for collection creation
   * @param {Array} csvData Original CSV data
   * @param {Array} geocodingResults Results from geocoding API
   * @returns {Array} Enhanced location data with coordinates
   */
  prepareLocationsWithGeocode(csvData, geocodingResults) {
    return csvData.map((row, index) => {
      const geocodingResult = geocodingResults.find(r => r.index === index);
      
      if (geocodingResult && geocodingResult.success) {
        // Add geocoding info to the row data
        return {
          ...row,
          _geocoding_coordinates: geocodingResult.coordinates,
          _geocoding_formatted_address: geocodingResult.formatted_address,
          _geocoding_confidence: geocodingResult.confidence,
          _geocoding_success: true
        };
      } else {
        // Mark as failed geocoding
        return {
          ...row,
          _geocoding_success: false,
          _geocoding_error: geocodingResult?.error || 'Unknown geocoding error'
        };
      }
    });
  },

  /**
   * Filter out failed geocoding results for collection creation
   * @param {Array} locationsWithGeocode Enhanced location data
   * @returns {Array} Only successfully geocoded locations
   */
  filterSuccessfulGeocode(locationsWithGeocode) {
    return locationsWithGeocode.filter(location => location._geocoding_success);
  }
};